import { useEffect, useState } from 'react';

type DemoCell =
  | 'alive'
  | 'burning'
  | 'burned'
  | 'selected'
  | 'selected-burning'
  | 'burn-action';

/**
 * Mini 5×5 demos — real games use a 10×10 grid with 5 starting fires at the
 * same positions each day. Each frame is a valid successor of the previous one:
 * burned cells never revert, fire only spreads to adjacent alive cells, and
 * pre-burns char instantly to gray (never burning).
 */
const DEMO_GRID = 5;

// Shared daily fire positions: (1,1), (2,2), (2,4), (4,0), (4,3)
const F = 'burning' as const;
const a = 'alive' as const;
const b = 'burned' as const;

export const CONTAINER_DEMO_FRAMES: DemoCell[][][] = [
  // 0 — Today's puzzle: 5 fires ignite
  [
    [a, a, a, a, a],
    [a, F, a, a, a],
    [a, a, F, a, F],
    [a, a, a, a, a],
    [F, a, a, F, a],
  ],
  // 1 — Your turn: click one alive tree
  [
    [a, a, a, a, a],
    [a, F, 'burn-action', a, a],
    [a, a, F, a, F],
    [a, a, a, a, a],
    [F, a, a, F, a],
  ],
  // 2 — Pre-burn chars instantly (gray) — fires unchanged
  [
    [a, a, a, a, a],
    [a, F, b, a, a],
    [a, a, F, a, F],
    [a, a, a, a, a],
    [F, a, a, F, a],
  ],
  // 3 — Fire turn: every flame chars, adjacent trees catch fire
  [
    [a, a, a, F, a],
    [a, b, b, F, a],
    [a, a, b, F, b],
    [a, a, F, a, a],
    [b, a, a, b, a],
  ],
  // 4 — Blaze contained: no fire left, rank by % forest saved
  [
    [a, a, a, b, a],
    [a, b, b, b, a],
    [a, a, b, b, b],
    [a, a, b, a, a],
    [b, a, a, b, a],
  ],
];

export const CONTAINER_DEMO_STEPS = [
  'Today\'s puzzle — 5 fires on a 10×10 grid',
  'Your turn — click one alive tree',
  'Pre-burn chars instantly — gray, never on fire',
  'Fire spreads — flames char, then jump outward',
  'Contain the blaze — rank by % forest saved',
];

export const SURVIVOR_DEMO_FRAMES: DemoCell[][][] = [
  // 0 — Today's puzzle: 5 fires already burning before you pick
  [
    [a, a, a, a, a],
    [a, F, a, a, a],
    [a, a, F, a, F],
    [a, a, a, a, a],
    [F, a, a, F, a],
  ],
  // 1 — Pick trees you believe will survive
  [
    ['selected', a, a, a, 'selected'],
    [a, F, a, a, a],
    [a, a, F, a, F],
    ['selected', a, a, a, a],
    [F, a, a, F, a],
  ],
  // 2 — Simulation runs: fire spreads automatically
  [
    ['selected', a, a, F, 'selected'],
    [a, b, F, a, a],
    [a, a, b, a, b],
    ['selected', a, F, a, a],
    [b, a, F, b, a],
  ],
  // 3 — A picked tree catches fire
  [
    ['selected', a, a, b, 'selected-burning'],
    [a, b, b, a, a],
    [a, a, b, a, b],
    ['selected', a, a, b, a],
    [b, a, b, b, a],
  ],
  // 4 — It chars out — instant loss
  [
    ['selected', a, a, b, b],
    [a, b, b, a, a],
    [a, a, b, a, b],
    ['selected', a, a, b, a],
    [b, a, b, b, a],
  ],
];

export const SURVIVOR_DEMO_STEPS = [
  'Today\'s puzzle — 5 fires already burning',
  'Pick trees you believe will survive',
  'Start — the blaze spreads automatically',
  'A pick catches fire',
  'It chars out — instant loss',
];

const FRAME_MS = 2800;

function cellClassName(cell: DemoCell): string {
  const base = 'cell howto-demo-cell';
  switch (cell) {
    case 'alive':
      return `${base} cell-alive`;
    case 'burning':
      return `${base} cell-burning`;
    case 'burned':
      return `${base} cell-burned`;
    case 'selected':
      return `${base} cell-portfolio-selected`;
    case 'selected-burning':
      return `${base} cell-burning`;
    case 'burn-action':
      return `${base} cell-alive howto-demo-cell-burn-action`;
    default:
      return base;
  }
}

export function ModeDemoLoop({
  frames,
  steps,
  gridSize = DEMO_GRID,
}: {
  frames: DemoCell[][][];
  steps: string[];
  gridSize?: number;
}) {
  const [frameIndex, setFrameIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setFrameIndex((current) => (current + 1) % frames.length);
    }, FRAME_MS);
    return () => window.clearInterval(id);
  }, [frames.length]);

  const frame = frames[frameIndex];
  const stepLabel = steps[frameIndex] ?? '';

  return (
    <div className="game-type-demo">
      <p className="howto-demo-step" aria-live="polite">
        {stepLabel}
      </p>
      <div className="howto-demo-grid-wrap">
        <div
          className="howto-demo-grid howto-demo-grid--live"
          style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)` }}
          aria-hidden
        >
          {frame.flatMap((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={cellClassName(cell)}
              />
            ))
          )}
        </div>
      </div>
      <div className="howto-demo-dots" aria-hidden>
        {frames.map((_, index) => (
          <span
            key={index}
            className={`howto-demo-dot${index === frameIndex ? ' howto-demo-dot--active' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}
